import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { BookOpen, Scissors, Ruler, Lightbulb, Settings, ArrowRight, PlayCircle } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const HowToUsePage = () => {
  usePageTitle('How to Use Our Products', 'Tips and guides for getting the most out of your Stitch Your Dash sewing tools and craft supplies.');

  const guides = [
    {
      icon: Scissors,
      title: 'Rotary Cutters',
      tips: [
        'Always cut away from your body for safety',
        'Use a self-healing cutting mat to protect surfaces',
        'Replace blades when they become dull',
        'Store with safety guard engaged'
      ]
    },
    {
      icon: Ruler,
      title: 'Seam Guides',
      tips: [
        'Position the magnetic guide on your sewing machine bed',
        'Align fabric edge with the guide for consistent seams',
        'Adjust position for different seam allowances',
        'Clean regularly for best magnetic hold'
      ]
    },
    {
      icon: Lightbulb,
      title: 'LED Light Strips',
      tips: [
        'Position lights to eliminate shadows on your work area',
        'Use the dimmer to adjust brightness to your preference',
        'Daylight LEDs reduce eye strain during long sessions',
        'USB power allows flexible placement options'
      ]
    },
    {
      icon: Settings,
      title: 'Sewing Machines',
      tips: [
        'Thread the machine according to the diagram',
        'Test stitch on scrap fabric before starting',
        'Clean and oil regularly for smooth operation',
        'Start with basic stitches before advanced features'
      ]
    }
  ];

  const beginnerTips = [
    'Start with simple projects to build confidence',
    'Keep your workspace organized and well-lit',
    'Measure twice, cut once - accuracy is key',
    'Don\'t be afraid to make mistakes - they\'re learning opportunities',
    'Join online sewing communities for support and inspiration'
  ];

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Hero with Banner Image */}
        <section className="relative bg-warmBrown-800 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/11482124/pexels-photo-11482124.jpeg?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-900 via-warmBrown-900/90 to-warmBrown-800/70" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-20" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-2xl">
              <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-6">
                <BookOpen className="w-8 h-8 text-rose-400" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4">How to Use Our Products</h1>
              <p className="text-warmBrown-300 text-lg">Tips and guides to help you get the most out of your sewing tools and create beautiful projects.</p>
            </div>
          </div>
        </section>

        {/* Product Guides */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl text-warmBrown-900 text-center mb-12">Product Usage Guides</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {guides.map((guide, index) => (
                <div key={index} className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-rose-100 rounded-2xl flex items-center justify-center">
                      <guide.icon className="w-6 h-6 text-rose-500" />
                    </div>
                    <h3 className="font-display text-xl text-warmBrown-900">{guide.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {guide.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-cream-100 rounded-full flex items-center justify-center text-rose-500 font-semibold text-sm flex-shrink-0">{i + 1}</span>
                        <span className="text-warmBrown-600">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Beginner Tips */}
        <section className="py-16 lg:py-20 bg-cream-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <PlayCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
              <h2 className="font-display text-3xl text-warmBrown-900 mb-4">Tips for Beginners</h2>
              <p className="text-warmBrown-600">New to sewing? Here are some tips to help you get started on your creative journey.</p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
              <ul className="space-y-4">
                {beginnerTips.map((tip, index) => (
                  <li key={index} className="flex items-center gap-4 p-4 bg-rose-50 rounded-2xl">
                    <span className="w-8 h-8 bg-rose-500 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">{index + 1}</span>
                    <span className="text-warmBrown-700 font-medium">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl text-warmBrown-900 mb-6">Ready to Start Creating?</h2>
            <p className="text-warmBrown-600 mb-8">Browse our collection of quality sewing tools and find everything you need for your next project.</p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20 group"
            >
              Shop Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowToUsePage;
